import React from 'react'
import styled from 'styled-components'
import { Container } from '@material-ui/core'
import Annex2Table from './Annex2Table'
import Annex1Table from './Annex1Table'
import CollectionDisclosureTable from './CollectionDisclosureTable'
import PrivacyRightsTable from './PrivacyRightsTable'
import ScrollToTop from 'components/ScrollToTop'
import { mediaQueries } from 'devices'
import { H2Bold, Text } from 'components/Typography/Basic'
import useDarkMode from 'use-dark-mode'

const PrivacyAndPolicy = () => {
  const darkMode = useDarkMode()
  const isDark = darkMode.value

  return (
    <ScrollToTop>
      <Wrapper isDark={isDark}>
        <H2Bold className="title">Privacy and Policy</H2Bold>
        <section>
          <Text as="h6">The last update was made on March 28, 2022.</Text>
          <Text as="p">
            The following policy ("Policy") explains how W3D Domains Inc. and
            its related companies ("Company") gather, use, and share personal
            information about users of W3DDomains.com (the "Site"). Similarly,
            this Policy applies to any other website that links to it. Websites
            that link to different statements are not covered by this policy.
          </Text>
        </section>
        <section>
          <Text as="h6">We collect the following information</Text>
          <Text as="p">
            In addition to your email address and phone number, we may collect
            other information directly from you on our Site. In particular, when
            you request a 3rd party account to be verified, such as a DNS or
            Twitter handle, we collect this information and may also log it to
            the blockchain. We may enrich the information we have about you with
            data from other sources. The information we get from this Site may
            include this. Your computer and your information are automatically
            logged. We track information about your use of and actions on our
            Site, such as your browser type, pages viewed, length of time spent
            on a page, and access times. We may log information using "cookies."
            Cookies are small data files stored on your hard drive by a website.
            Our Site may use both session cookies (which expire when you close
            your web browser) and persistent cookies in order to provide you
            with a more personalized and interactive experience. We collect this
            type of information to make the Site more useful to you and to
            tailor the experience to meet your unique interests.
          </Text>
        </section>
        <section>
          <Text as="h6">USE OF PERSONAL INFORMATION</Text>
          <Text as="p">
            Through our software, you can broadcast a transaction to the
            blockchain network using the information you enter on our Site. We
            cannot control how others may use your personal information once it
            has been broadcast to the blockchain network. Think of your
            blockchain domain as a "Public Profile" like any other social
            account, and know that others will have access to the information
            you direct us to share with them. Specifically, if you post
            information about yourself in our management portal and then save it
            to the blockchain, the information is now publicly available
            forever. W3D Domains uses your personal information as follows:
          </Text>
          <ul>
            <Text as="li">
              We use your personal information for the operation, maintenance,
              and improvement of our Sites, products, and services.
            </Text>
            <Text as="li">
              Our customer service team uses your personal information to
              respond to comments and questions.
            </Text>
            <Text as="li">
              In addition to sending confirmations, invoices, technical notices,
              updates, security alerts, and support and administrative messages,
              we may use your personal information to contact you.
            </Text>
            <Text as="li">
              In addition, we use your personal information to inform you about
              promotions, upcoming events, and other news about products and
              services we offer.
            </Text>
            <Text as="li">
              In order to protect, investigate, and deter fraudulent,
              unauthorized, and illegal activities, we use your personal
              information.
            </Text>
          </ul>
        </section>
        <section>
          <Text as="h6">SHARING OF PERSONAL INFORMATION</Text>
          <Text as="p">We may share personal information as follows:</Text>
          <ul>
            <Text as="li">
              Personal information may be shared with your consent. Publishing
              personal information to the blockchain, for example, allows you to
              share that information with others. Once this information is
              published, it may not be able to be unpublished (as one of the
              main purposes of blockchain systems is to enable data provenance
              through accurate and immutable record keeping).
            </Text>
            <Text as="li">
              When we sell or transfer our business or assets, or negotiate a
              business deal involving the sale or transfer of all or part of our
              business, we may share personal information. Mergers,
              acquisitions, financings, or bankruptcy proceedings can all fall
              under this category.
            </Text>
            <Text as="li">
              For legal, safety, and protection purposes, we may share personal
              information. Depending on the law, we may be required to share
              information.
            </Text>
            <Text as="li">
              Legal requests and legal processes may require us to share
              information.
            </Text>
            <Text as="li">
              W3D Domains Inc., our agents, customers, and others may share
              information to protect their rights and property. Our agreements,
              policies, and terms of service must be enforced.
            </Text>
            <Text as="li">
              Information may be shared in an emergency. Protecting our
              employees, agents, customers, or anyone else is part of this
              responsibility.
            </Text>
            <Text as="li">
              Information may be shared with those who need it to do their jobs.
            </Text>
            <Text as="li">
              In addition, we may share aggregated data with others for their
              own purposes.
            </Text>
          </ul>
        </section>
        <section>
          <Text as="h6">INFORMATION CHOICES AND CHANGES</Text>
          <Text as="p">
            In our marketing emails, we tell you how to "opt-out." If you opt
            out, we may still send you non-marketing emails. In addition to
            marketing emails, we send you emails about your accounts and our
            business relationships. Please contact our Contact Information below
            if you have questions about your personal information. Your personal
            information can be updated, your contact preferences can be changed,
            and you can opt out of our sharing with others. Many browsers are
            set to accept cookies until you change your settings. Depending on
            your browser settings, you can either: (i) change them so you
            receive a notification when a cookie is received, allowing you to
            choose whether or not to accept it; (ii) disable existing cookies;
            or (iii) set your browser to reject cookies automatically. It is
            important to note that this may negatively affect your experience on
            our sites, as some features and services may not function properly.
            All cookies may not be able to be deleted or blocked depending on
            your mobile device and operating system. You may also set your
            e-mail options to prevent the automatic downloading of images that
            may contain technologies that would allow us to know whether you
            have accessed our e-mail and performed certain functions with it.
            Your mobile device's settings may also allow you to disable
            interest-based advertising by selecting "limit ad tracking" (iOS) or
            "opt out of interest-based ads" (Android). Unless you change your
            settings, many browsers accept cookies. If you remove or reject our
            cookies, it could affect how our Site works for you.
          </Text>
        </section>
        <section>
          <Text as="h6">LINKS TO THIRD-PARTY WEBSITES AND SERVICES</Text>
          <Text as="p">
            In order to provide you with convenience, our Service may provide
            links to third-party websites or services linked to or forming the
            service, including the information or content contained therein. You
            are subject to the rules and policies of the third party, not ours,
            when you browse or interact with any other website or service. If
            you use a third-party website or service, you do so at your own
            risk. Whenever you provide personal information to a site or
            service, you should review its privacy policies.
          </Text>
        </section>
        <section>
          <Text as="h6">RAKUTEN ADVERTISING</Text>
          <Text as="p">
            Rakuten Advertising may collect personal information from you when
            you interact with our digital properties, including IP addresses,
            digital identifiers, information about your web browsing and app
            usage, and how you interact with our properties and ads for a
            variety of purposes, such as personalizing offers and
            advertisements, and performing analytics about how you engage with
            websites and ads. Below are links to more information about how your
            personal data is collected, used, and sold.
          </Text>
          <ul>
            <Text as="li">
              <a
                href="https://rakutenadvertising.com/legal-notices/services-privacy-policy/"
                target="_blank"
              >
                https://rakutenadvertising.com/legal-notices/services-privacy-policy/
              </a>
            </Text>
            <Text as="li">
              <a
                href="https://rakutenadvertising.com/legal-notices/services-privacy-rights-request-form/"
                target="_blank"
              >
                https://rakutenadvertising.com/legal-notices/services-privacy-rightsrequest-form/
              </a>
            </Text>
          </ul>
        </section>
        <section>
          <Text as="h6">CHILDREN'S PRIVACY</Text>
          <Text as="p">
            Children under 16 are not permitted to use our services. Information
            about children under 16 is not knowingly collected or solicited by
            us. The information will be deleted as soon as possible if it has
            been inadvertently collected from a child under 16. In the event
            that you believe that we may have collected information from a child
            under 16, please contact us at W3Ddomains.com.
          </Text>
        </section>
        <section>
          <Text as="h6">CHANGES TO PRIVACY POLICY</Text>
          <Text as="p">
            From time to time, we may modify this Privacy Policy at our sole
            discretion. If we make any material changes in the way we treat your
            personal data, we will notify you by sending an email to your
            primary address or by posting a prominent notice on our Site. This
            Privacy Policy should be reviewed periodically by you. The changes
            will become effective as of the date of publication unless otherwise
            stated.
          </Text>
        </section>
        <section>
          <Text as="h6">CONTACT INFORMATION</Text>
          <Text as="p">
            We welcome your comments or questions about this Privacy Policy.
            Contact us at (888) 508-1038 or at legal@w3ddomains.com.
          </Text>
        </section>
        <section>
          <Text as="h6">
            GLOBAL APPLICABILITY AND REGION-SPECIFIC DISCLOSURES
          </Text>
          <Text as="p">
            Site visitors, Service users, and other companies and users
            worldwide are covered by this privacy policy. Depending on the
            country, region, or state where we process personal information, we
            may choose to provide additional disclosures. Here are some
            disclosures you may need to know:
          </Text>
        </section>
        <section>
          <Text as="h6">CALIFORNIA PRIVACY RIGHTS</Text>
          <Text as="p">
            Please see the "California Privacy Disclosures" section below for
            more information if you are a California resident.
          </Text>
        </section>
        <section>
          <Text as="h6">NEVADA PRIVACY RIGHTS</Text>
          <Text as="p">
            A Nevada resident may opt out of future sales of certain covered
            information that a website operator has collected or will collect
            about them under Chapter 603A of the Nevada Revised Statutes. In
            accordance with Chapter 603A, we do not sell your personal
            information. However, if you still wish to submit such a request,
            please contact us at legal@w3ddomains.com.
          </Text>
        </section>
        <section>
          <Text as="h6">
            EUROPEAN ECONOMIC AREA, UNITED KINGDOM OR SWITZERLAND
          </Text>
          <Text as="p">
            If you are located in the European Economic Area (“EEA”), the United
            Kingdom or Switzerland, or otherwise engage with W3D Domain’s
            European operations, please see the “Privacy Disclosures for the
            European Economic Area, United Kingdom and Switzerland” for
            additional European-specific privacy disclosures, including what
            constitutes your personal information, the lawful bases we rely on
            to process your personal information, how we use cookies when you
            access our Services from the EEA, UK or Switzerland and your rights
            in respect of your personal information.
          </Text>
        </section>
        <section>
          <Text as="h6">NOTE FOR INTERNATIONAL VISITORS</Text>
          <Text as="p">
            Personal information may be transferred to, stored and processed in
            a country other than the one in which it was collected. For example,
            the Sites are primarily hosted in the United States and provided
            from there. The country to which personal data is transferred may
            not provide the same level of protection as the country from which
            it was transferred.
          </Text>
        </section>
        <section>
          <UnderLinedText as="h6">
            California Privacy Disclosures
          </UnderLinedText>
          <Text as="p">
            This section applies to you if you are a resident of the state of
            California. California law requires us to disclose certain
            information regarding the categories of information we collect. The
            California Consumer Privacy Act (the "CCPA") defines "personal
            information" for the purposes of this section. No information
            related to our employees, contractors, applicants, or other
            personnel is covered by this section.
          </Text>
        </section>
        <section>
          <UnderLinedText as="h6" className="grey_text">
            Collection, Disclosure, and Sale of Personal Information
          </UnderLinedText>
          <Text as="p" className="grey_text" fontStyle="italic">
            In the last 12 months, we have not sold personal information about
            you, but we have collected and disclosed for a business purpose the
            following categories and examples of specific pieces of personal
            information:
          </Text>
          <CollectionDisclosureTable />
          <Text as="p" fontStyle="italic" className="grey_text">
            <span
              style={{
                fontWeight: 700
              }}
            >
              The term "selling" of personal information is not used by us.
            </span>
            Our third-party providers analyze and optimize our online services,
            offer more relevant content and ads to users of our online services
            and other third parties, and perform other services related to
            analytics or advertising in accordance with these Disclosures and
            our Privacy Policy.
          </Text>
          <Text as="p" fontStyle="italic" className="grey_text">
            In accordance with their privacy notices, these third-party
            businesses may use the information they collect directly from you
            for their own purposes, including providing services to other third
            parties, including advertising networks. To the extent that these
            practices qualify as "sales" under the CCPA, you may have the right
            to opt out as described in the "Your Privacy Rights" section below.
          </Text>
        </section>
        <section>
          <UnderLinedText as="h6" className="grey_text">
            Sources of Personal Information
          </UnderLinedText>
          <Text as="p" fontStyle="italic" className="grey_text">
            As described in the What We Collect and Use of Personal Information
            sections of our Privacy Policy, we collect personal information
            directly from you when you provide it to us, automatically when you
            visit our websites and other online services, and from service
            providers and other third parties. For representatives of our
            clients, vendors, service providers and other third parties, we also
            collect personal information directly from you and from the entity
            you represent.
          </Text>
        </section>
        <section>
          <UnderLinedText as="h6" className="grey_text">
            Personal Information Collection Purposes
          </UnderLinedText>
          <UnderLinedText as="h6" className="grey_text">
            Recipients of Personal Information
          </UnderLinedText>
          <Text as="p" fontStyle="italic" className="grey_text">
            We share personal information with third parties as described in the
            Sharing of Personal Information section of our Privacy Policy. In
            addition, we share your personal information with representatives of
            our clients, vendors, service providers, and other third parties.
          </Text>
        </section>
        <section>
          <UnderLinedText as="h6" className="grey_text">
            Your Privacy Rights
          </UnderLinedText>
          <Text as="p" fontStyle="italic" className="grey_text">
            As a California resident, you may be able to exercise the following
            rights in relation to the Personal Information about you that we
            have collected (subject to certain limitations at law):
          </Text>
          <PrivacyRightsTable />
          <UnderLinedText as="h6" className="grey_text">
            Submitting Requests
          </UnderLinedText>
          <UnderLinedText as="h6" className="grey_text" fontWeight="400">
            To Exercise Your Right to Know or Right to Deletion
          </UnderLinedText>
          <Text as="p" fontStyle="italic" className="grey_text">
            To submit a request to exercise your right to know or right to
            deletion, please contact us at (888) 508-1038 or at
            legal@w3ddomains.com.
          </Text>
          <Text as="p" fontStyle="italic" className="grey_text">
            Before processing your request, we will need to verify your identity
            and confirm you are a resident of the State of California. This
            process may require us to request additional personal information
            from you, including, but not limited to, your email, validation of
            your residential or business address, product website name, and/or
            date of last interaction with customer service.
          </Text>
          <Text as="p" fontStyle="italic" className="grey_text">
            In certain circumstances, we may decline a request to exercise the
            right to know and/or right to deletion, particularly where we are
            unable to verify your identity.
          </Text>
        </section>
        <section>
          <UnderLinedText as="h6" className="grey_text" fontWeight="400">
            Authorized Agents
          </UnderLinedText>
          <Text as="p" fontStyle="italic" className="grey_text">
            In certain circumstances, you are permitted to use an authorized
            agent (as that term is defined by the CCPA) to submit requests on
            your behalf through the designated methods set forth in these
            Disclosures where we can verify the authorized agent’s authority to
            act on your behalf by:
          </Text>
          <ol>
            <Text as="li" fontStyle="italic" className="grey_text">
              For requests to know or delete personal information::
              <ol>
                <Text as="li" className="grey_text">
                  receiving a power of attorney valid under the laws of
                  California from you or your authorized agent; or
                </Text>
                <Text as="li" className="grey_text">
                  receiving sufficient evidence to show that you have:
                  <ol>
                    <Text as="li" className="grey_text">
                      provided the authorized agent signed permission to act on
                      your behalf;
                    </Text>
                    <Text as="li" className="grey_text">
                      verified your own identity directly with us pursuant to
                      the instructions set forth in these Disclosures; and
                    </Text>
                    <Text as="li" className="grey_text">
                      directly confirmed with us that you provided the
                      authorized agent permission to submit the request on your
                      behalf.
                    </Text>
                  </ol>
                </Text>
              </ol>
            </Text>
            <Text as="li" fontStyle="italic" className="grey_text">
              For requests to opt-out of personal information “sales”: receiving
              a signed permission demonstrating your authorized agent has been
              authorized by you to act on your behalf.
            </Text>
          </ol>
        </section>
        <section>
          <ol>
            <Text as="li" ontStyle="italic" className="grey_text">
              <Text as="h6" fontStyle="italic" className="grey_text">
                PERSONAL DATA WE COLLECT FROM YOU WHEN YOU W3D DOMAIN’S EUROPEAN
                SERVICES
              </Text>
              <Text as="p" fontStyle="italic" className="grey_text">
                As described in our Privacy Policy under the section entitled
                “What We Collect,” the table at Annex 1 details the categories
                of personal information we collect when you use our European
                Services, as well as the legal basis for our processing of your
                personal information.
              </Text>
            </Text>
            <Text as="li" ontStyle="italic" className="grey_text">
              <Text as="h6" fontStyle="italic" className="grey_text">
                AUTOMATICALLY, WE COLLECT PERSONAL INFORMATION ABOUT YOU. We
                also automatically collect personal information about how you
                access and use the European Services, as well as information
                about the device you use to access them.{' '}
                <span
                  style={{
                    fontWeight: 400
                  }}
                >
                  For example, we may collect:
                </span>
              </Text>
              <ol>
                <Text as="li" className="grey_text">
                  information about the features you use and the pages you view
                  on the European Services;
                </Text>
                <Text as="li" className="grey_text">
                  information about your device (such as your IP address, device
                  identifier, device type, model and manufacturer); and
                </Text>
                <Text as="li" className="grey_text">
                  information about your usage patterns (such as how often you
                  use the W3D Domain European Services and your language
                  settings).
                </Text>
              </ol>
            </Text>
            <Text as="li" fontStyle="italic" className="grey_text">
              <p>
                This information is used to provide you with the features and
                functionality of the European Services, to monitor and improve
                the European Services, and to develop new services. In Annex 2,
                we provide further information about the types of personal
                information we collect about you automatically and how we use
                it. As well as listing the legal basis for processing and
                recipients of your personal information, the table lists the
                legal basis for processing. Combined with the information we
                collect automatically, we may link or combine your personal
                information.
              </p>
              <p>
                All personal information we collect may be anonymized and
                aggregated (so that it cannot be directly associated with you).
                We may use anonymized information for purposes that include
                testing our IT systems, research, data analysis, improving the
                W3D Domains European Services. We may also share such anonymized
                and aggregated information with others.
              </p>
            </Text>
            <Text as="li" fontStyle="italic" className="grey_text">
              <h6>HOW LONG WILL WE STORE YOUR PERSONAL INFORMATION</h6>
              <p>
                We will usually store the personal information we collect about
                you for no longer than necessary for the purposes set out in
                Annex 1 and Annex 2, in accordance with our legal obligations
                and legitimate business interests. Where we retain information
                pursuant to Legal Obligation, we retain such information for 7
                years.
              </p>
              <p>
                The criteria used to determine the period for which personal
                information about you will be retained varies depending on the
                legal basis under which we process the personal information:
              </p>
              <ol>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Legitimate Interests
                  </span>
                  . Where we are processing personal information based on our
                  legitimate interests, we generally will retain such
                  information for a reasonable period of time based on the
                  particular interest, taking into account the fundamental
                  interests and the rights and freedoms of data subjects.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Consent.
                  </span>{' '}
                  Where we are processing personal information based on your
                  consent, we generally will retain the information until you
                  withdraw your consent, or otherwise for the period of time
                  necessary to fulfil the underlying agreement with you or
                  provide you with the applicable service for which we process
                  that personal information.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Contract.
                  </span>{' '}
                  Where we are processing personal information based on
                  contract, we generally will retain the information for the
                  duration of the contract plus some additional limited period
                  of time that is necessary to comply with law or that
                  represents the statute of limitations for legal claims that
                  could arise from the contractual relationship.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Legal Obligation.
                  </span>{' '}
                  Where we are processing personal information based on a legal
                  obligation, we generally will retain the information for the
                  period of time necessary to fulfil the legal obligation.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Legal Claim.
                  </span>{' '}
                  We may need to apply a "legal hold" that retains information
                  beyond our typical retention period where we face threat of
                  legal claim. In that case, we will retain the information
                  until the hold is removed, which typically means the claim or
                  threat of claim has been resolved.
                </li>
              </ol>
            </Text>
            <Text as="li" fontStyle="italic" className="grey_text">
              In all cases, in addition to the purposes and legal bases, we
              consider the amount, nature and sensitivity of the personal
              information, as well as the potential risk of harm from
              unauthorised use or disclosure of your personal information.
            </Text>
            <Text as="li" fontStyle="italic" className="grey_text">
              <h6>RECIPIENTS OF PERSONAL INFORMATION</h6>
              <p>
                In addition to the recipients listed in Annexes 1 and 2, we may
                also share your personal information with the following (as
                required in accordance with the uses set out in Annexes 1 and
                2):
              </p>
              <ol>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Service providers and advisors:
                  </span>{' '}
                  Your personal information may be shared with third parties who
                  perform services for us or on our behalf, such as legal,
                  accounting, mailing, email, chat, fraud prevention, web
                  hosting, and analytics.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Affiliates.
                  </span>{' '}
                  The other companies are owned by or under common ownership
                  with W3D Domains, including our subsidiaries (e.g., any
                  company we own or control) and our ultimate holding company
                  (e.g., any company that owns or controls us). Your personal
                  information will be used by these companies in the same manner
                  as it is used by us under these Privacy Disclosures
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Purchasers and third parties in connection with a business
                    transaction:
                  </span>{' '}
                  your personal information may be disclosed to third parties in
                  connection with a transaction, such as a merger, sale of
                  assets or shares, reorganisation, financing, change of control
                  or acquisition of all or a portion of our business.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Law enforcement,
                  </span>{' '}
                  regulators, and other parties for legal reasons: W3D Domains
                  may share your personal information with third parties as
                  required by law or if we reasonably believe that such action
                  is necessary to (i) comply with the law and law enforcement
                  requests, (ii) detect and investigate illegal activities and
                  breaches of agreements, including our Terms, and/or (iii)
                  exercise or protect the rights, property, or personal safety
                  of W3D Domains or its users.regulators and other parties for
                  legal reasons: we may share your personal information with
                  third parties as required by law or if we reasonably believe
                  that such action is necessary to (i) comply with the law and
                  the reasonable requests of law enforcement; (ii) detect and
                  investigate illegal activities and breaches of agreements,
                  including our Terms; and/or (iii) exercise or protect the
                  rights, property, or personal safety of W3D Domains, its users
                  or others.
                </li>
              </ol>
            </Text>
            <Text as="li" fontStyle="italic" className="grey_text">
              <h6>MARKETING AND ADVERTISING</h6>
              <p>
                From time to time we may contact you with information about our
                services, including sending you marketing messages and asking
                for your feedback on our services. Most marketing messages we
                send will be by email. For some marketing messages, we may use
                personal information we collect about you to help us determine
                the most relevant marketing information to share with you. We
                will only send you marketing messages if you have given us your
                consent to do so. You can withdraw your consent at a later date
                by clicking on the unsubscribe link at the bottom of our
                marketing emails or by updating your preferences via your
                account on the Site.
              </p>
            </Text>
            <Text as="li" fontStyle="italic" className="grey_text">
              <h6>STORING AND TRANSFERRING YOUR PERSONAL INFORMATION</h6>
              <p>
                <span
                  style={{
                    fontWeight: 700
                  }}
                >
                  Security
                </span>
                . We implement appropriate technical and organizational measures
                to protect your personal information from accidental or unlawful
                destruction, loss, change, or damage. Personal information we
                collect will be stored on secure servers by the cloud hosting
                provider. We will never contact you by email or phone to request
                credit card or debit card information.
              </p>
              <p>
                <span
                  style={{
                    fontWeight: 700
                  }}
                >
                  International transfer of your personal information
                </span>
                . If we or our third-party service providers operate outside
                your jurisdiction, your personal information may be transferred
                to and stored there. Residents of the European Economic Area,
                the United Kingdom, or Switzerland may have their personal
                information processed outside their region, including in the
                United States. We ensure that: (i) the data is transferred to
                countries with equivalent levels of protection; or (ii)
                appropriate safeguards are in place, such as the European
                Commission standard data protection clause.
              </p>
              <p>
                If you would like to know more about these safeguards, please
                contact us using the information provided at the bottom of these
                Privacy Disclosures.
              </p>
            </Text>
            <Text as="li" fontStyle="italic" className="grey_text">
              <span
                style={{
                  fontWeight: 700
                }}
              >
                Detecting and reducing fraud may also require us to use personal
                data about you
              </span>
              . and credit risk.
            </Text>
            <Text as="li" fontStyle="italic" className="grey_text">
              <h6>YOUR RIGHTS IN RESPECT OF YOUR PERSONAL INFORMATION</h6>
            </Text>
          </ol>
          <Text as="ol" fontStyle="italic" className="grey_text">
            <li>
              The following rights apply to the personal information we hold
              about you in accordance with applicable privacy law:
              <ol>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Right of access
                  </span>{' '}
                  Among the rights you have are:
                  <ol>
                    <li>
                      We will confirm whether and where your personal
                      information is being processed;
                    </li>
                    <li>
                      How we determine the applicable retention periods and what
                      categories of personal information we are processing;
                    </li>
                    <li>
                      The categories of recipients with whom we may share your
                      personal information; and
                    </li>
                    <li>
                      We will provide you with a copy of the personal
                      information we have about you.
                    </li>
                  </ol>
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Right of portability.
                  </span>{' '}
                  In certain circumstances, you have the right to receive a copy
                  of the personal information you have provided to us in a
                  structured, commonly used, machine-readable format that can be
                  reused or transferred.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Right to rectification.
                  </span>{' '}
                  If you believe your personal information is inaccurate or
                  incomplete, you have the right to request its rectification
                  without undue delay.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    The right to be erased.
                  </span>{' '}
                  When it is not justified to continue processing your personal
                  information, you may request that we erase that information
                  without undue delay.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Right to restriction.
                  </span>{' '}
                  You have the right, in some circumstances, to require us to
                  limit the purposes for which we process your personal
                  information if the continued processing of the personal
                  information in this way is not justified, such as where the
                  accuracy of the personal information is contested by you.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Right to withdraw consent.
                  </span>{' '}
                  There are certain circumstances where we require your consent
                  to process your personal information. In these instances, and
                  if you have provided consent, you have the right to withdraw
                  your consent. If you withdraw your consent, this will not
                  affect the lawfulness of our use of your personal information
                  before your withdrawal.
                </li>
              </ol>
            </li>
            <li>
              <p
                style={{
                  fontWeight: 700
                }}
              >
                In addition, you have the right to object to any processing
                based on our legitimate interests where you have grounds
                relating to your particular situation. There may be compelling
                reasons for continuing to process your personal information, and
                we will assess and inform you if that is the case. If you do not
                want to participate in marketing activities, you can object to
                them for any reason.
              </p>
              <p>
                A complaint can also be made to your local data protection
                authority. The European Union's data protection authority can be
                contacted via this link if you are based there. If you are based
                in the UK or Switzerland, your local data protection authorities
                are the UK Information Commissioner's Offic lode
                <a href="https://ico.org.uk/global/contact-us/" target="_blank">
                  (https://ico.org.uk/global/contact-us/)
                </a>{' '}
                and the Swiss Federal Data Protection and Information
                Commissioner
                <a
                  href="https://www.edoeb.admin.ch/edoeb/en/home/the-fdpic/contact/address.html"
                  target="_blank"
                >
                  (https://www.edoeb.admin.ch/edoeb/en/home/the-fdpic/contact/address.html).
                </a>{' '}
              </p>
              <p>
                If you wish to exercise one of these rights, please contact us
                using the contact details at the end of these Privacy
                Disclosures.
              </p>
              <p>
                Due to the confidential nature of data processing we may ask you
                to provide proof of identity when exercising the above rights.
                This can be done by providing a scanned copy of a valid identity
                document or a signed photocopy of a valid identity document.
              </p>
            </li>
            <li>
              <h6>
                COOKIES AND SIMILAR TECHNOLOGIES USED ON OUR EUROPEAN SERVICES
              </h6>
              <p>
                Our European Services uses cookies and similar technologies such
                as pixels and Local Storage Objects (LSOs) like HTML5 (together
                "cookies") to distinguish you from other users of our European
                Services. This helps us to provide you with a good experience
                when you browse our European Services and also allows us to
                monitor and analyse how you use and interact with our European
                Services so that we can continue to improve our European
                Services. It also helps us and our advertising partners to
                determine products and services that may be of interest to you,
                in order to serve you targeted advertisements.
              </p>
              <p>
                By saving your user ID and other preferences, cookies allow us
                to personalize your experience on our European Services. A
                cookie is a small data file that we save on your computer's hard
                drive.
              </p>
              <p>The following types of cookies are used by us:</p>
              <ol className="larger_text">
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Strictly necessary cookies.
                  </span>{' '}
                  These are cookies that are required for the operation of our
                  European Services. They include, for example, cookies that
                  enable you to log into secure areas of our European Services.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Analytical/performance cookies.
                  </span>{' '}
                  Using them, we can identify and count the number of visitors
                  and see how they move around our European Services. By
                  improving our European Services, for example, we can ensure
                  that users can easily find what they are looking for.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Functionality cookies.
                  </span>{' '}
                  When you return to our European Services, these cookies will
                  recognize you. By doing so, we can customize our content for
                  you, greet you by name and remember your preferences (such as
                  the language or region you prefer).
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Targeting cookies.
                  </span>{' '}
                  We can use these cookies to track the pages you visit and the
                  links you click when you use our European Services. Using
                  these cookies, you can keep track of which pages you visit and
                  which links you click while using our European Services.
                  advertising displayed on it, and We will tailor the marketing
                  messages we send to you based on your interests. Third parties
                  who provide us with services may also receive this
                  information.
                </li>
                <li>
                  <span
                    style={{
                      fontWeight: 700
                    }}
                  >
                    Third party cookies.
                  </span>{' '}
                  You should be aware that third parties may use their own
                  cookies tags when you click on advertisements or links on our
                  European Services. It is the responsibility of these third
                  parties to set their own cookie and privacy policies.
                </li>
              </ol>
            </li>
            <li>
              <p>
                Our European Services use a variety of cookies. For more
                information, please see Annex 3. We will only place cookies on
                your device if you give us your consent, except for those
                strictly necessary to operate our European Services. When you
                access our European Services for the first time, we will ask you
                what cookies you accept.
              </p>
              <p>
                In most browsers, you can also block certain cookies by changing
                your cookie settings.
              </p>
              <p>
                Depending on your mobile device and operating system, you may
                not be able to delete or block all cookies. Please note that if
                you choose to refuse all cookies you may not be able to use the
                full functionality of our European Services. These settings will
                typically be found in the "options" or "preferences" menu of
                your browser. In order to understand these settings, the
                following links may be helpful, otherwise you should use the
                "Help" option in your browser for more details.
              </p>
              <p>
                <span className="larger_text">
                  For more information about cookies and other similar
                  technologies, please visit www.allaboutcookies.org or the
                  Network Advertising Initiative online resources at
                  www.networkadvertising.org.
                </span>
                Cookies and tracking technologies may also be used for
                advertising purposes by us and by our third-party partners.
              </p>
              <p>
                Tracking pixels may be used in our emails to identify whether
                and when you have opened, read, and clicked on any links in the
                email we sent you. Deleting or blocking cookies may not be
                effective for all tracking technologies. We use this information
                to measure the effectiveness of our marketing emails, determine
                if you've opened and read any important administrative emails we
                send you, and make them more relevant to your interests.
              </p>
              <p>
                Most popular email clients will allow you to block these pixels
                by disabling certain external images in emails. You can choose
                whether emails will display "remote images", "remote content" or
                "images" by default through the settings on your email client.
              </p>
              <p>
                You can also download and install extensions that block pixels
                and other tracking technologies in some browsers.
              </p>
              <section>
                <Text as="h6" className="grey_text">
                  ANNEX 1 - PERSONAL INFORMATION YOU PROVIDE US
                </Text>
                <Annex1Table />
              </section>
              <section>
                <Text as="h6" className="grey_text">
                  ANNEX 2 - PERSONAL INFORMATION COLLECTED AUTOMATICALLY
                </Text>
                <Annex2Table />
              </section>
            </li>
            <li>
              HTML5 uses technologies such as Local Storage Objects (LSOs).
            </li>
          </Text>
        </section>
      </Wrapper>
    </ScrollToTop>
  )
}

export default PrivacyAndPolicy

const Wrapper = styled(Container)`
  margin-top: 80px;
  font-size: 18px;
  margin-bottom: 50px;
  overflow-x: hidden;
  .grey_text {
    color: ${({ isDark }) => (isDark ? '#D3D3D3' : '#6B6B6B')};
    font-style: italic;

    .dark-mode {
      color: red;
    }
  }
  .title {
    text-align: center;
    font-family: 'poppins';
    margin-bottom: 80px;
  }

  section {
    margin-top: 40px;
  }
  a {
    color: blue;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
      
    ${mediaQueries.phone} {
      display: block;
    }
  }
  .larger_text {
    font-size: 20px;
  }
  h6 {
    margin-top: 1em;
    margin-bottom: 1em;
    font-size: 18px;
  }
  hr {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
    margin-left: auto;
    margin-right: auto;
  }
  ol {
    margin-top: 1em;
    margin-bottom: 1em;
    padding-left: 40px;

    ${mediaQueries.phone} {
      padding-left: 20px;
    }
  }
  p {
    margin-top: 0.6em;
    margin-bottom: 0.6em;
  }
  ul {
    margin-top: 1em;
    margin-bottom: 1em;
    padding-left: 40px;
    ${mediaQueries.phone} {
      padding-left: 20px;
    }
  }
  li {
    display: list-item;
    margin-top: 5px;
  }

  .MuiTableContainer-root {
    border: ${({ isDark }) =>
      isDark ? '1px solid #D3D3D3' : '1px solid #6B6B6B'};


    .MuiTableCell-root {
    border: ${({ isDark }) =>
      isDark ? '1px solid #D3D3D3' : '1px solid #6B6B6B'};
      vertical-align: text-top !important;
      color: ${({ isDark }) => (isDark ? '#D3D3D3' : '#6B6B6B')};

      h6 {
        font-style: italic;
      }
      p, li {
        font-weight: 400;
        font-style: italic;
      }

  }
`

const UnderLinedText = styled(Text)`
  text-decoration: underline;
`
